import { FC, useCallback, useEffect } from 'react';
import { PRODUCT_NUMBER, TERM } from 'config/domain';
import { QuestionnaireFormValues } from 'types/ui';
import { R } from 'config/app';
import {
  Row,
  ShieldIcon,
  Spacer,
  Text,
  Title,
  useDeviceDetection,
} from 'internalLibrary';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'api/errors/ValidationError';
import Cookies from 'js-cookie';
import layout from '../../../../scss/layout.module.scss';
import QuestionnaireForm from './QuestionnaireForm';
import styles from './Questionnaire.module.scss';
import useApplicationConstraintsQuery from '../../../hooks/useApplicationConstraintsQuery';
import useClientQuery from 'ui/hooks/useClientQuery';
import useIoBlackbox from 'ui/hooks/useIoBlackbox';
import useLoanApplicationProcess from 'ui/components/LoanApplicationProcessLayout/LoanApplicationProcessProvider/useLoanApplicationProcess';
import useLoanApplicationQuery from 'ui/hooks/useLoanApplicationQuery';
import usePostLoanApplicationMutation from 'ui/hooks/usePostLoanApplicationMutation';
import usePutLoanApplicationMutation from 'ui/hooks/usePutLoanApplicationMutation';
import useQuestionnaire from './useQuestionnaire';

const QuestionnaireContent: FC = () => {
  const { t } = useTranslation();
  const { status, questionnaireFormSubmitted, questions, questionnaireId } =
    useQuestionnaire();
  const { loanAmount } = useLoanApplicationProcess();
  const {
    mutateAsync: postApplication,
    isLoading: isLoadingPostApplication,
    error,
    isError,
  } = usePostLoanApplicationMutation();
  const { mutateAsync: putApplication, isLoading: isLoadingPutApplication } =
    usePutLoanApplicationMutation();
  const { loanApplication, isFetching } = useLoanApplicationQuery();
  const { applicationConstraints } = useApplicationConstraintsQuery();
  const { data: clientData } = useClientQuery();
  const { isMobile } = useDeviceDetection();
  const ioBlackBox = useIoBlackbox();
  const navigate = useNavigate();

  const handleSubmitQuestionnaire = useCallback(
    async (values: QuestionnaireFormValues) => {
      await questionnaireFormSubmitted(values, questionnaireId);

      if (loanApplication?.status === 'OPEN') {
        if (clientData?.markedForManualIdentification) {
          navigate(R.FirstLoanPendingManual);
          return;
        }

        navigate(R.FirstLoanIdentificationMethod);
        return;
      }

      const affiliateProvider = Cookies.get('affiliateProvider');
      const affiliateToken = Cookies.get('affiliateToken');
      const affiliatePartner = Cookies.get('affiliatePartner');

      const { rejected: createRejected } = await postApplication({
        amount: loanAmount,
        term: applicationConstraints?.termInterval?.defaultValue || TERM,
        source: isMobile ? 'MOBILE' : 'DESKTOP',
        productNumber: PRODUCT_NUMBER,
        termUnit: 'DAYS',
        ioBlackBox,
        affiliateInfo:
          affiliateProvider || affiliatePartner || affiliateToken
            ? {
                provider: affiliateProvider,
                token: affiliateToken,
                partner: affiliatePartner,
              }
            : undefined,
      });

      if (createRejected) {
        navigate(R.FirstLoanRejected);
        return;
      }

      const { rejected: confirmRejected } = await putApplication({});

      if (confirmRejected) {
        navigate(R.FirstLoanRejected);
        return;
      }

      navigate(R.FirstLoanIdentificationMethod);
    },
    [
      loanAmount,
      loanApplication?.status,
      clientData?.markedForManualIdentification,
      navigate,
      questionnaireId,
    ],
  );

  useEffect(() => {
    if (!isError) return;

    if (
      error instanceof ValidationError &&
      error.fieldErrors.length > 0 &&
      error.fieldErrors.find((e) => e.message === 'Application is rejected')
    ) {
      navigate(R.FirstLoanRejected);
      return;
    }
  }, [isError, error]);

  return (
    <div className={layout['width-lg']}>
      <Spacer
        marginBottom={{ default: '40px', s: '35px' }}
        className={styles.heading}
      >
        <Spacer marginBottom={{ default: '20px' }}>
          <Title
            text={t('questionnaire_title')}
            data-test="css-Contact-Data-Title"
          />
        </Spacer>
        <Spacer marginBottom={{ default: '30px', s: '19px' }} marginTop="9px">
          <Text
            text={t('questionnaire_subtitle')}
            className={styles.subtitle}
          />
        </Spacer>
      </Spacer>

      {questions ? (
        <QuestionnaireForm
          onSubmit={handleSubmitQuestionnaire}
          questions={questions}
          isSubmitting={
            status === 'loading' ||
            isLoadingPostApplication ||
            isLoadingPutApplication ||
            isFetching
          }
        />
      ) : (
        <div />
      )}

      <div className={layout['width-lg']}>
        <Spacer marginTop="45px">
          <Row>
            <div>
              <ShieldIcon />
            </div>
            <Spacer paddingLeft="10px">
              <Text
                text={t('questionnaire_securityNotice')}
                kind="span"
                level={3}
                data-test="css-Secure-Info"
              />
            </Spacer>
          </Row>
        </Spacer>
      </div>
    </div>
  );
};

export default QuestionnaireContent;
